import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage,injectIntl } from 'react-intl';
import commonFunctions from '../../utils/commonFunctions';
import API from '../../utils/API'
import Table from '../../components/Table/Table'
import editIcon from '../../assets/icons/table/edit.svg'
import trashIcon from '../../assets/icons/table/trash.svg'
import RolesModal from './RolesModal'
import ConfirmModal from '../../components/Modals/ConfirmModal'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import constants from '../../utils/constants'
import InputValidation from '../../utils/InputValidation'
import useInfiniteScroll from '../../components/common/useInfiniteScroll';

function Roles(props) {
  const navigate = useNavigate();
  const { closeModal } = commonFunctions;
  const { user, intl } = props;
  const permissionIds = user.permissions.map((permission)=>{
    return permission.id
  })
  if(!permissionIds.includes(constants.permissionIds.userEdit)){
    navigate('/forbidden');
  };

  // Give state to all components of the dataMap(explained later) you want to render
  const [data, setData] = React.useState([]);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const [company, setCompany] = React.useState("");
  const [dataCount, setDataCount] = React.useState(0);
  const [totalDataCount, setTotalDataCount] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState({});
  const [sortByCol, setSortByCol] = React.useState('name');
  const [sortByAsc, setSortByAsc] = React.useState(true);
  const [startIndexForTable, setStartIndexForTable] = React.useState(0);

  const [roles, setRoles] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [checkedRoles, setCheckedRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchValue, setSearchValue] = useState('');
  // Track the type of action to perform on the role
  const [actionType, setActionType] = useState('');

  // Roles modal field validation states
  const [submitted, setSubmitted] = useState(false);
  const [roleNameValid, setRoleNameValid] = useState('');
  const [roleDescValid, setRoleDescValid] = useState('');
  const [permissionsValid, setPermissionsValid] = useState('');

  // Checks if user has Alert Plus license
  const [hasAlertPlusLicense, setHasAlertPlusLicense] = useState(false);
  // Checks if user has Orchestrate license
  const [hasOrchestrateLicense, setHasOrchestrateLicense] = useState(false);
    // Checks if user has Message Manager license
    const [hasMessageManagerLicense, setHasMessageManagerLicense] = useState(false);

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  // Perform field validations using a single useEffect
  useEffect(() => {
    validateRoleName(roleName)
    validateRoleDesc(roleDesc)
    validatePermissions(selectedPermissions)
  }, [roleName, roleDesc, selectedPermissions]);

  const dataMap = React.useMemo(() => {
    const map = {data: {}, ids: [], selectedIds: {}, filteredIds: []};
    loadTable(map);
    return map;
    
  }, [])

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    startIndexForTable,
    setStartIndexForTable,
    pageSize: 100, 
    sortByCol,
    setSortByCol,
    sortByAsc, 
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  let startIndex = React.useMemo(()=>{
    return 0;
  }, [])

  useEffect(()=>{ 
    if(searchValue !== ""){
      tableRef.current.search(dataMap, startIndex)
    }else{
      sortMap(dataMap);
      setData(generateDataSlice(dataMap, startIndex))
    }
  }, [sortByCol, sortByAsc])

  /**
   * Load table with data
   */
  async function loadTable(map, resetSelection=true) {
    //Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if(resetSelection){
      map.selectedIds = {}
    }

    // Fetch licenses
    const licenseResponse = await API.getLicenses();

    // Check if any license contains 'Alert Plus' or 'Orchestrate' feature_id
    const hasAlertPlusLicense = licenseResponse.data.some(
      (license) => license.feature_ids.includes('AlertPlus') 
    );

    const hasOrchestrateLicense = licenseResponse.data.some(
      (license) => license.feature_ids.includes('Orchestrate') 
    );

    const hasMessageManagerLicense = licenseResponse.data.some(
      (license) => license.feature_ids.includes('MessageManager') 
    );

    // Fetch roles and permissions
    const rolesResponse = await API.getRoles();
    const permissionsResponse = await API.getPermissions();

    rolesResponse.data.forEach((role) => {
      // Check if the role's permission should be filtered based on licenses
      let shouldFilterRole = false;
      if(role.permissions.length > 0 && (!hasAlertPlusLicense || !hasOrchestrateLicense || !hasMessageManagerLicense)) {
         shouldFilterRole = role.permissions.every((permission) => 
          (!hasAlertPlusLicense && 
            (permission.id === constants.permissionIds.aPlusTeacherApp ||
            permission.id === constants.permissionIds.aPlusEdit)
          ) ||
          (!hasOrchestrateLicense && (permission.id === constants.permissionIds.orchestrateEdit)) ||
          (!hasMessageManagerLicense && 
            (permission.id === constants.permissionIds.msgManagerEdit ||
            permission.id === constants.permissionIds.msgManagerFrontDeskView)
          )
        );
      }

      if (!shouldFilterRole) {
        map.data[role.id] = {
          id: role.id,
          name: role.name,
          description: role.description,
          features: role.features,
          featureNames: role.features.map((feature) => { return feature.name }).join(', '),
          permissions: role.permissions,
          permissionNames: role.permissions.map((permission) => { return intl.formatMessage({id: permission.id}) }).join(', '),
          users: role.users,
          settings: role.settings,
          checked: false,
        };
      }
    });
    // console.log('map data:', map.data)
    map.filteredIds = Object.keys(map.data);
    map.ids = Object.keys(map.data);
  
    // Call React setState to render a section of our data
    if(searchValue !== ""){
      tableRef.current.search(map)
    }else{
      sortMap(map);
      setData(generateDataSlice(map));
      setDataCount(map.sortedIds.length);
    }   
    setSelectedIds(map.selectedIds);
    setTotalDataCount(map.ids.length);
    setDataLoaded(true);

    // Set available permissions
    var permissionDict = {};
    permissionsResponse.data.forEach((permission) => {
      const shouldFilterPermission =
        (!hasAlertPlusLicense &&
          (permission.id === constants.permissionIds.aPlusTeacherApp ||
            permission.id === constants.permissionIds.aPlusEdit)) ||
        (!hasOrchestrateLicense && permission.id === constants.permissionIds.orchestrateEdit) ||
        (!hasMessageManagerLicense && 
          (permission.id === constants.permissionIds.msgManagerEdit ||
            permission.id === constants.permissionIds.msgManagerFrontDeskView)
        );
       console.log('permission:', permission);
       console.log('should filter perm', shouldFilterPermission)
      if(!shouldFilterPermission) {
        permissionDict[permission.id] = permission;
      }
      // permissionDict[permission.id] = permission;
    });
    setAvailablePermissions(permissionDict);
  }

  const validateRoleName = (value) => {
    setRoleNameValid(InputValidation.validateNameLength(value));
  };
  
  const validateRoleDesc = (value) => {
    setRoleDescValid(InputValidation.validateRoleDesc(value));
  };

  const validatePermissions = (permissions) => {
    setPermissionsValid(InputValidation.validatePermissions(permissions));
  };

  const handleRoleNameChange = (e) => {
    setRoleName(e.target.value);
    validateRoleName(e.target.value);
  };
  
  const handleRoleDescChange = (e) => {
    setRoleDesc(e.target.value);
    validateRoleDesc(e.target.value);
  };

const handlePermissionChange = (e) => {
  const permission = e.target.value;
  let updatedPermissions;

  if (e.target.checked) {
    updatedPermissions = [...selectedPermissions, permission];
  } else {
    updatedPermissions = selectedPermissions.filter((id) => id !== permission);
  }

  setSelectedPermissions(updatedPermissions);
  validatePermissions(updatedPermissions);
};
  

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Perform field validations
    console.log('validating:', roleName, roleDesc, selectedPermissions)
    validateRoleName(roleName);
    validateRoleDesc(roleDesc);
    validatePermissions(selectedPermissions);

    if (
      !roleNameValid &&
      !roleDescValid &&
      !permissionsValid
    ) {
      if (selectedRole) {
        handleEditRole();
      } else {
        handleAddRole();
      }
    } else {
      console.log(roleNameValid, roleDescValid, permissionsValid);
      setError(<FormattedMessage id="SUBMIT_ERROR" />);
    }
  };

  /**
   * Handle add permission
   * @param {*} permission
   */
  function handleAddPermission(permission) {
    setSelectedPermissions((prevPermissions) => [...prevPermissions, permission]);
  }

  /**
   * Handle remove permission
   * @param {*} e 
   */
  function handleRemovePermission(permission) {
    setSelectedPermissions((prevPermissions) =>
      prevPermissions.filter((p) => p !== permission)
    );
  }

  /**
   * Handle add button click 
   * @param {*} id 
   */
  function handleAddClick(e) {
    e.preventDefault();
    resetModal();
  }

  /**
   * Handle edit click on Roles table
   * @param {*} id 
   */
  function handleEditClick(e, id) {
    e.preventDefault();
    resetModal();
    let targetRole = dataMap.data[id];
    setSelectedRole(targetRole);
    setRoleName(targetRole.name);
    setRoleDesc(targetRole.description);
    setSelectedPermissions(targetRole.permissions.map((permission)=>{return permission.id}));
  }

  /**
   * Handle click to delete a specific role.
   * @param {string} id 
   */
  function handleSingleDelete(e, id) {
    e.preventDefault();
    let targetRole = dataMap.data[id];
    console.log('clicked role:', targetRole);
    setSelectedRole(targetRole);
    setActionType('single');
  }

  /**
   * Handle click mass delete action.
   */
  function handleMassDelete(e) {
    e.preventDefault();
    setActionType('multiple');
  }

  /**
   * Handle deleting roles
   */
  const handleDeleteRoles = async () => {
    setSuccess('');
    setError('');

    try {
      if (actionType === 'single') {
        if (selectedRole && selectedRole.name !== 'Account Owner') {
          await API.deleteRole(selectedRole.id);
          setSuccess(<FormattedMessage id="ROLE_DELETE_SUCCESS" />);
        } else {
          setError(<FormattedMessage id="DELETE_ROLES_ERROR" />);
        }
      } else {
        const rolesToDelete = Object.keys(dataMap.selectedIds)
          .filter((id) => dataMap.selectedIds[id])
          .map((id) => dataMap.data[id])
          .filter((role) => role.name !== 'Account Owner');

        if (rolesToDelete.length === 0) {
          setError(<FormattedMessage id="DELETE_ROLES_ERROR" />);
          return;
        }

        await Promise.all(rolesToDelete.map((role) => API.deleteRole(role.id)));

        setSuccess(<FormattedMessage id="COUNT_ROLES_DELETE_SUCCESS" values={{ count: <b>{rolesToDelete.length}</b> }} />);
      }
      closeModal('confirmModal');
    } catch (err) {
      setError(<FormattedMessage id="DELETE_ROLES_FAILED_INFO" values={{ info: <b>{err.message}</b> }} />);
    }

    loadTable(dataMap);
  };

  /**
   * Handle API call to add/save role
   */
  function handleAddRole() {
    API.addRole(selectedPermissions, roleName.trim(), roleDesc.trim())
      .then((res)=>{
        loadTable(dataMap);
        setSuccess(<FormattedMessage id="ROLE_ADDED_SUCCESS" />)
        closeModal('rolesModal');
      })
      .catch((err)=>{
        setError(err.message);
        console.error(err.message);
      })
  }
  
  /**
   * Make API call to edit role
   */
  function handleEditRole() {
    // console.log('selected role', selectedRole)
    // console.log('selected permissions', selectedPermissions, roleName, roleDesc)
    API.editRole(selectedRole.id, selectedPermissions, roleName.trim(), roleDesc.trim())
      .then((res)=>{
        loadTable(dataMap)
        setSuccess(<FormattedMessage id="ROLE_UPDATED_SUCCESS" />);
        closeModal('rolesModal');
      })
      .catch((err)=>{
        setError(err.message);
        console.error(err.message);
      })
  }

  /**
   * Reset fields of user modal
   */
    function resetModal() {
      setSelectedRole(null);
      setSelectedPermissions([]);
      setRoleName('');
      setRoleDesc('');
      setError('');
      setSuccess('');
      setSubmitted(false);
    }

  // Initialize table columns
  const tableColumnArray = [
    {
      Header: 'ROLE',
      id: 'name',
      dataIndex: 'name',
      accessor: 'name',
      isVisible: true,
    },
    {
      Header: 'FEATURES',
      id: 'featureNames',
      accessor: 'featureNames',
      isVisible: true,
    },
    {
      Header: 'PERMISSIONS',
      id: 'permissionNames',
      accessor: 'permissionNames',
      isVisible: true,
    },
    {
      Header: '#_USERS',
      id: 'users',
      accessor: 'users',
      isVisible: true,
      width: 60
    },
    {
      Header: 'DESCRIPTION',
      id: 'description',
      accessor: 'description',
      isVisible: true,
    },
    {
      Header: '',
      id: 'actions',
      isVisible: true,
      Cell: ({ row }) => (
        <div className="actions">
          <button
            id="editRoleBtn"
            className="btn"
            data-bs-toggle="modal"
            data-bs-target={`#rolesModal`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleEditClick) handleEditClick(e, row.original.id);
            }}
          >
            <img src={editIcon} className="actionIcon editAction" alt={<FormattedMessage id="EDIT" />} />
          </button>
          <button
            id="deleteRoleBtn"
            className="btn delete-action"
            data-bs-toggle="modal"
            data-bs-target={`#confirmModal`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleSingleDelete) handleSingleDelete(e, row.original.id);
            }}
          >
            <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DELETE" />} />
          </button>
        </div>
      ),
    }
  ]

  const tableColumns = React.useMemo(
  
    () => tableColumnArray, []);

  const buttons = {
    toolbarRightButtons: (
      <>
        <button
          id="addRolesBtn"
          type="button"
          className="btn btn-filled"
          data-bs-toggle="modal"
          data-bs-target={`#rolesModal`}
          onClick={handleAddClick}
        >
          <span>
            <FormattedMessage id="ADD_ROLES" defaultMessage="Add Roles" />
          </span>
        </button>
      </>
    ),
  }

  const tableProps = {
    pageType: constants.pageType.UserRoles,
    modalId: 'rolesModal',
    columns: tableColumns,
    data, setData,
    dataMap,
    // Data Handling props
    generateDataSlice,
    setDataCount,
    sortMap,
    dataCount,
    totalDataCount,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    setSortBy,
    success, setSuccess,
    error, setError,
    sortByCol,
    sortByAsc,
    handleAddClick,
    handleDeleteRoles,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    // Action column props
    handleEditClick,
    handleSingleDelete,
    buttons,
  } 
  
  // Props to pass to our modal component
  const modalProps = {
    selectedRole, setSelectedRole,
    roleName, 
    roleDesc,
    selectedPermissions,
    availablePermissions,
    handleAddPermission,
    handleRemovePermission,
    handleAddRole,
    handleEditRole,
    success,
    error,
    // Validation Props
    submitted,
    roleNameValid, 
    roleDescValid, 
    // Handlers 
    handleRoleNameChange,
    handleRoleDescChange,
    handlePermissionChange,
    handleSubmit
  }

  const confirmModalProps = {
    actionType,
    checkedItems: Object.keys(selectedIds),
    handleDelete: handleDeleteRoles,
    closeModal,
    dataType: 'role',
  }

  return (
    <>
      <ConfirmModal {...confirmModalProps} />
      <RolesModal {...modalProps} />
      <Table {...tableProps} /> 
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(injectIntl(Roles))